import type { Pose, Transition } from '../controls/types';
import type { PainterProjectionState } from '../render/types';
import { clamp, easeInOutCubic, smoothstep } from '../core/math';

/**
 * Maximum distance from the painter position before the projection effect
 * reaches zero. Set to 200m because the painter camera is ~2km from the city;
 * at 200m offset the view is clearly different from the painting.
 */
export const PAINTER_DISTANCE_THRESHOLD_M = 200;

/**
 * Maximum angular deviation from the painter's view direction before the
 * effect reaches zero. Set to 15 degrees (~0.26 rad) because even small
 * rotations make the perspective noticeably different from the painting.
 */
export const PAINTER_ANGLE_THRESHOLD_RAD = (15 * Math.PI) / 180;

function distance3(
  a: readonly [number, number, number],
  b: readonly [number, number, number]
): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function normalizeAngle(a: number): number {
  let result = a % (2 * Math.PI);
  if (result > Math.PI) result -= 2 * Math.PI;
  if (result < -Math.PI) result += 2 * Math.PI;
  return result;
}

function angleDifference(a: number, b: number): number {
  return Math.abs(normalizeAngle(a - b));
}

/**
 * Computes the painter projection amount based on how close the current pose
 * is to the painter's original viewpoint. Returns 1 at the painter pose and
 * smoothly fades to 0 as position or view direction diverge.
 *
 * The amount is the product of two smoothstep falloffs:
 * - Position: fades from 1 to 0 over 0 to PAINTER_DISTANCE_THRESHOLD_M
 * - View direction: fades from 1 to 0 over 0 to PAINTER_ANGLE_THRESHOLD_RAD
 *
 * Using the product ensures the effect only appears when both position and
 * direction are close to the painter's viewpoint.
 */
export function computePainterProjectionAmount(current: Pose, painter: Pose): number {
  const dist = distance3(current.position, painter.position);
  const yawDiff = angleDifference(current.yaw, painter.yaw);
  const pitchDiff = Math.abs(current.pitch - painter.pitch);
  const totalAngleDiff = Math.sqrt(yawDiff * yawDiff + pitchDiff * pitchDiff);

  const distanceFactor = 1 - smoothstep(0, PAINTER_DISTANCE_THRESHOLD_M, dist);
  const angleFactor = 1 - smoothstep(0, PAINTER_ANGLE_THRESHOLD_RAD, totalAngleDiff);

  return distanceFactor * angleFactor;
}

/**
 * Portrait framing eases out over half the flight, independently of the geometry corrections.
 * Coupling lens fitting to those corrections makes the portrait lens jump when they switch off.
 */
export const PAINTER_GLIDE_SHARE = 0.5;

/*
 * The lens keeps fitting the painting as the camera leaves or approaches its viewpoint.
 */
function glideAmount(transition: Transition, painter: Pose): number {
  const along = easeInOutCubic(clamp(transition.elapsed / transition.duration, 0, 1));
  if (transition.endsAtPainterView) return smoothstep(1 - PAINTER_GLIDE_SHARE, 1, along);
  const leaves = transition.glide && computePainterProjectionAmount(transition.from, painter) === 1;
  return leaves ? 1 - smoothstep(0, PAINTER_GLIDE_SHARE, along) : 0;
}

export function painterFramingAmount(
  current: Pose,
  painter: Pose,
  transition: Transition | null = null
): number {
  const near = computePainterProjectionAmount(current, painter);
  return transition ? Math.max(near, glideAmount(transition, painter)) : near;
}

/**
 * A moving flight uses the real model throughout. A finished-frame dissolve handles the switch
 * at either end; interpolating these corrections makes the buildings and landscape change shape.
 */
export function createPainterProjectionState(
  current: Pose,
  painter: Pose,
  transition: Transition | null = null
): PainterProjectionState {
  const painterFlight =
    transition &&
    (transition.endsAtPainterView ||
      computePainterProjectionAmount(transition.from, painter) === 1);
  return {
    amount: painterFlight ? 0 : computePainterProjectionAmount(current, painter),
    cameraPosition: current.position,
  };
}
